import { useMemo } from "react";
import { notesListParams } from "@/features/notes/lib/list-query-params";
import type {
	NotesAdapter,
	NotesCreateVars,
	NotesDeleteVars,
	NotesUpdateVars,
} from "@/features/notes/types";
import {
	useCreateAssetNote,
	useDeleteAssetNote,
	useUpdateAssetNote,
} from "@/services/asset/asset.mutations";
import { assetInstanceQueryOptions } from "@/services/asset/asset.options";

/**
 * Pure list query options for asset notes (route loaders).
 */
export function createAssetNotesListQueryOptions(args: {
	assetId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return assetInstanceQueryOptions.notes.list(
		args.assetId,
		args.start,
		args.count,
		notesListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Asset-scoped notes adapter (hooks + query option factories).
 */
export function useAssetNotesAdapter(assetId: string): NotesAdapter {
	const createNote = useCreateAssetNote();
	const updateNote = useUpdateAssetNote();
	const deleteNote = useDeleteAssetNote();

	return useMemo(
		(): NotesAdapter => ({
			kind: "asset",
			entityId: assetId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createAssetNotesListQueryOptions({
					assetId,
					organizationId,
					start,
					count,
				}),
			createMutation: {
				mutateAsync: (vars: NotesCreateVars) =>
					createNote.mutateAsync({
						assetId: vars.entityId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => createNote.reset(),
				isPending: createNote.isPending,
				error: (createNote.error as Error | null) ?? null,
			},
			updateMutation: {
				mutateAsync: (vars: NotesUpdateVars) =>
					updateNote.mutateAsync({
						assetId: vars.entityId,
						noteId: vars.noteId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => updateNote.reset(),
				isPending: updateNote.isPending,
				error: (updateNote.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: NotesDeleteVars) =>
					deleteNote.mutateAsync({
						assetId: vars.entityId,
						noteId: vars.noteId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteNote.reset(),
				isPending: deleteNote.isPending,
				error: (deleteNote.error as Error | null) ?? null,
			},
		}),
		[assetId, createNote, deleteNote, updateNote],
	);
}
